/**
 * React Query-based initialization service.
 *
 * This module provides initialization functions that use React Query for data
 * fetching and caching for caching. This is the target state for
 * the app initialization for the React Query migration.
 */

import { mixpanel } from "common/analytics";
import { queryClient } from "common/queryClient";
import { queryKeys } from "common/queryKeys";
import { fetchApi } from "common/utils/Api";
import {
  markDeviceAsRegistered,
  setupPushNotificationsAndRegisterDevice,
  shouldRegisterDevice,
} from "common/utils/deviceRegistration";
import { getRedirectUrlWithToken, redirect } from "common/utils/Hooks";
import { default as decode } from "jwt-decode";
import { Farm } from "model/farmTypes";
import {
  getAllArableFieldsFromCache,
  getAllFieldsFromCache,
  getArableFieldsWithNoTilesFromArray,
  getFieldsWithNoTilesFromArray,
  getLatestArableFieldTilesFromArray,
  getLatestFieldTilesFromArray,
} from "model/fieldHelpers";
import { fetchAllAssociatedUsersQuery } from "model/useAccessibleUsersQueries";
import {
  fetchAnimalGroupsByIdQuery,
  resetAnimalGroupsQueryCache,
} from "model/useAnimalGroupQueries";
import {
  fetchArableFieldsByFarmIdQuery,
  resetArableFieldsQueryCache,
} from "model/useArableFieldQueries";
import {
  fetchAccessibleFarmerDashboardsQuery,
  fetchAccessibleFarmerProgrammesQuery,
  fetchFarmerDashboardsQuery,
  fetchProjectDashboardsQuery,
} from "model/useDashboardQueries";
import { fetchDefaultStockClassesQuery } from "model/useDefaultStockClassesQueries";
import { fetchFarmByIdQuery, resetFarmsQueryCache } from "model/useFarmQueries";
import {
  fetchFieldsByIdQuery,
  resetFieldsQueryCache,
} from "model/useFieldQueries";
import { fetchFileEntriesForUserQuery } from "model/useFileEntryQueries";
import { fetchProgrammeActionsQuery } from "model/useProgrammeActionsQueries";
import { fetchQuickAssessmentResultsQuery } from "model/useQuickAssessmentQueries";
import { User } from "model/userTypes";
import {
  fetchStockClassesByIdQuery,
  resetStockClassesQueryCache,
} from "model/useStockClassQueries";
import { fetchSubmittedActionsQuery } from "model/useSubmittedActionsQueries";
import {
  fetchTilesByIdsQuery,
  resetTilesQueryCache,
} from "model/useTileQueries";
import { fetchUserByIdQuery } from "model/useUserQueries";
import { useAppStore } from "stores/useAppStore";
import { useUIStore } from "stores/useUIStore";
import { JWT } from "./auth";
import {
  invalidateArableFieldData,
  invalidateFieldData,
} from "./fieldOperations";

// Progress steps: Sign in (1) -> Load data (2) -> Load farm fields (3) -> Load farm animals (4) -> Done (5)
const APP_LOADING_STEPS = 5;

/**
 * Initialize the application after login using React Query.
 * Validates the JWT token, fetches user data, and sets up the app state.
 *
 * Initializes the app after login.
 *
 * @param options - Optional configuration
 * @param options.skipLoadingStateReset - If true, don't set loading to idle at the end
 *   (useful when farm initialization will follow immediately)
 * @returns The result containing farms, user data, and whether farm init is needed
 */
export async function initializeAppRQ(options?: {
  skipLoadingStateReset?: boolean;
}): Promise<{
  farms?: Farm[];
  user?: User;
  needsFarmInit?: boolean;
}> {
  const { skipLoadingStateReset = false } = options ?? {};
  const {
    setLoadingStatus,
    setLoadingProgress,
    setLoading,
    setLocale,
    setUser,
    setAuthenticated,
  } = useAppStore.getState();

  setLoading("pending");
  setLoadingProgress(0, APP_LOADING_STEPS);

  setLoadingStatus("Signing in...");
  const token = window.localStorage.getItem("jwt");
  if (!token) {
    setLoading("idle");
    return {};
  }

  const jwtDecoded = decode<JWT>(token);

  let user: User;
  try {
    user = await fetchUserByIdQuery(jwtDecoded.user);
    // Store user in Zustand
    setUser(user);
    setLoadingProgress(1, APP_LOADING_STEPS);

    if (user && user.guid) {
      // Fetch file entries in background (non-blocking)
      void fetchFileEntriesForUserQuery(user.guid, queryClient).then(
        (fileEntries) => {
          queryClient.setQueryData(
            queryKeys.fileEntries.byUser(user.guid),
            fileEntries
          );
        }
      );
    }
  } catch (e) {
    setLoading("idle");
    throw e;
  }

  // Redirect only after sign in, to make sure the token is valid
  const redirectUrl = getRedirectUrlWithToken();
  if (user && user.guid && redirectUrl) {
    redirect();
    return {};
  }

  if (user.lang) {
    setLocale(user.lang);
  }
  const farmIds = user.farms;

  if (jwtDecoded.impersonated) {
    mixpanel.opt_out_tracking();
  }

  mixpanel.identify(jwtDecoded.user);

  // With Mixpanel's identity merge, we need to explicitly store the user guid as
  // a property and then use it in reports; because distinct id can be random uuids
  mixpanel.register({
    user_id: jwtDecoded.user,
    user_type: user.userType,
    gitsha: import.meta.env.VITE_GITSHA,
  });

  // Setup device registration for push notifications (non-blocking)
  if (shouldRegisterDevice()) {
    void setupPushNotificationsAndRegisterDevice()
      .then(() => markDeviceAsRegistered())
      .catch((error) =>
        console.error("Failed to setup device registration:", error)
      );
  }

  // ========== PARALLEL FETCH: All independent data at once ==========
  // These fetches have no dependencies on each other, so run them all in parallel
  setLoadingStatus("Loading your data...");

  const isCorporateUser =
    user?.corporations?.length && user?.corporations?.length > 0;

  // Build array of parallel fetch promises
  const parallelFetches: Promise<unknown>[] = [
    // Dashboards
    fetchFarmerDashboardsQuery().then((data) => {
      queryClient.setQueryData(queryKeys.dashboards.farmer, data);
      return data;
    }),
    fetchProjectDashboardsQuery().then((data) => {
      queryClient.setQueryData(queryKeys.dashboards.project, data);
      return data;
    }),
    // Programme data
    fetchProgrammeActionsQuery().then((data) => {
      queryClient.setQueryData(queryKeys.programmeActions.all, data);
      return data;
    }),
    fetchQuickAssessmentResultsQuery().then((data) => {
      queryClient.setQueryData(queryKeys.quickAssessment.results, data);
      return data;
    }),
    // Default stock classes
    fetchDefaultStockClassesQuery(queryClient),
  ];

  // Corporate user dashboards
  if (isCorporateUser) {
    parallelFetches.push(
      fetchAccessibleFarmerDashboardsQuery().then((data) => {
        queryClient.setQueryData(queryKeys.dashboards.accessibleFarmer, data);
        return data;
      })
    );
    parallelFetches.push(
      fetchAccessibleFarmerProgrammesQuery().then((data) => {
        queryClient.setQueryData(
          queryKeys.dashboards.accessibleFarmerProgrammes,
          data
        );
        return data;
      })
    );
  }

  // Submitted actions (requires user email)
  if (user.mail) {
    parallelFetches.push(
      fetchSubmittedActionsQuery(user.mail).then((data) => {
        queryClient.setQueryData(
          queryKeys.programmeActions.submitted(user.mail),
          data
        );
        return data;
      })
    );
  }

  // Associated users (v2 list endpoint - fetches all at once)
  parallelFetches.push(fetchAllAssociatedUsersQuery(queryClient));

  // Farms
  let farms: Farm[] = [];
  if (farmIds && farmIds.length > 0) {
    parallelFetches.push(
      Promise.all(
        farmIds.map((id) => fetchFarmByIdQuery(id, queryClient))
      ).then((fetchedFarms) => {
        farms = fetchedFarms;
        return farms;
      })
    );
  }

  // Execute all fetches in parallel
  await Promise.all(parallelFetches);
  setLoadingProgress(2, APP_LOADING_STEPS);

  // Load certification modal state from localStorage
  useUIStore.getState().loadCertificationProgramApplicationModalState();

  // Determine if farm initialization will be needed
  const hasFarms = farmIds && farmIds.length > 0;
  const needsFarmInit = hasFarms && !isCorporateUser;

  // Only set loading to idle if:
  // 1. Farm init won't follow (corporate users or no farms), OR
  // 2. Caller explicitly doesn't want us to skip the reset
  if (!skipLoadingStateReset || !needsFarmInit) {
    setLoading("idle");
  }
  setAuthenticated(true);

  if (!hasFarms) {
    return { user, needsFarmInit: false };
  }

  return { farms, user, needsFarmInit };
}

/**
 * Initialize a farm using React Query.
 * Fetches all farm data and populates React Query cache.
 * Optimized to fetch independent data in parallel.
 *
 * Initializes a specific farm.
 *
 * @param farm - The farm to initialize
 * @param options - Optional configuration
 * @param options.force - If true, re-initialize even if same farm is already loaded
 */
export async function initializeFarmRQ(
  farm: Farm,
  options?: { force?: boolean }
): Promise<void> {
  const {
    setLoadingStatus,
    setLoadingProgress,
    setCurrentFarmId,
    setLoading,
    currentFarmId,
    loading,
  } = useAppStore.getState();
  const { force = false } = options ?? {};

  // Skip re-initialization if:
  // 1. Same farm is already set as current
  // 2. We're not forcing a refresh
  // 3. We're not in the middle of loading (which means app init is in progress)
  if (currentFarmId === farm.guid && !force && loading !== "pending") {
    return;
  }

  // Check if we're switching to a different farm
  const isSwitchingFarms =
    currentFarmId !== null && currentFarmId !== farm.guid;

  // Set loading state to show loading screen during farm initialization
  // (may already be pending from app init, which is fine)
  setLoading("pending");
  // Set loading status - use farm name for personalized feedback
  setLoadingStatus(`Loading ${farm.name}...`);
  setCurrentFarmId(farm.guid);
  mixpanel.register({ farm_id: farm.guid });
  setLoadingProgress(2, APP_LOADING_STEPS); // Continue from app init progress (step 2)

  // Only reset farm-related caches when switching to a DIFFERENT farm
  // This prevents unnecessary refetching when navigating within the same farm
  if (isSwitchingFarms) {
    resetFieldsQueryCache(queryClient);
    resetArableFieldsQueryCache(queryClient);
    resetAnimalGroupsQueryCache(queryClient);
    resetStockClassesQueryCache(queryClient);
    resetTilesQueryCache(queryClient);
    resetFarmsQueryCache(queryClient);
  }

  // Store the farm in cache
  queryClient.setQueryData(queryKeys.farms.detail(farm.guid), farm);

  // Track completion of parallel operations for granular progress updates
  let fieldsLoaded = false;
  let animalsLoaded = false;

  const updateProgressStatus = () => {
    if (fieldsLoaded && animalsLoaded) {
      setLoadingStatus("Finishing up...");
      setLoadingProgress(4, APP_LOADING_STEPS);
    } else if (fieldsLoaded) {
      setLoadingStatus("Loading animals...");
      setLoadingProgress(3, APP_LOADING_STEPS);
    } else if (animalsLoaded) {
      setLoadingStatus("Loading fields...");
      setLoadingProgress(3, APP_LOADING_STEPS);
    }
  };

  // ========== Load all farm data in parallel ==========
  const farmDataPromises: Promise<unknown>[] = [];

  // Arable fields - update progress when complete
  farmDataPromises.push(
    fetchArableFieldsByFarmIdQuery(farm.guid, queryClient).then((result) => {
      fieldsLoaded = true;
      updateProgressStatus();
      return result;
    })
  );

  // Grass/pasture fields - mark as part of fields loading
  if (farm.fields && farm.fields.length !== 0) {
    farmDataPromises.push(
      fetchFieldsByIdQuery(farm.fields, queryClient).then((result) => {
        // Fields already marked as loaded by arable fields, but ensure it's set
        fieldsLoaded = true;
        updateProgressStatus();
        return result;
      })
    );
  }

  // Animal groups and their stock classes (chained to avoid separate phase)
  if (farm.animalGroups && farm.animalGroups.length > 0) {
    farmDataPromises.push(
      fetchAnimalGroupsByIdQuery(farm.animalGroups, queryClient).then(
        async (animalGroups) => {
          // Load stock classes for all animal groups
          const allStockClassIds = animalGroups.flatMap(
            (ag) => ag.stockClasses || []
          );
          if (allStockClassIds.length > 0) {
            await fetchStockClassesByIdQuery(allStockClassIds, queryClient);
          }
          animalsLoaded = true;
          updateProgressStatus();
          return animalGroups;
        }
      )
    );
  } else {
    // No animal groups to load, mark as complete
    animalsLoaded = true;
  }

  // Default stock classes (reference data)
  farmDataPromises.push(fetchDefaultStockClassesQuery(queryClient));

  // Start with "Loading fields..." since that's typically the first to start
  setLoadingStatus("Loading fields...");
  setLoadingProgress(3, APP_LOADING_STEPS);

  // Execute all in parallel
  await Promise.all(farmDataPromises);

  // ========== Background tasks (non-blocking) ==========
  // Load field tiles and growth rate in background
  void (async () => {
    // Get arable field tiles
    const arableFields = getAllArableFieldsFromCache(queryClient, farm.guid);
    const arableTileIds = getLatestArableFieldTilesFromArray(arableFields);

    // Get grass/pasture field tiles
    const fields = getAllFieldsFromCache(queryClient, farm.guid);
    const fieldTileIds = getLatestFieldTilesFromArray(fields);

    // Fetch all tiles in parallel (already batched internally)
    const tilePromises: Promise<unknown>[] = [];
    if (arableTileIds.length > 0) {
      tilePromises.push(fetchTilesByIdsQuery(arableTileIds, queryClient));
    }
    if (fieldTileIds.length > 0) {
      tilePromises.push(fetchTilesByIdsQuery(fieldTileIds, queryClient));
    }
    await Promise.all(tilePromises);

    // Invalidate fields with no tiles (mark them for re-processing)
    const arableFieldsWithNoTiles =
      getArableFieldsWithNoTilesFromArray(arableFields);
    arableFieldsWithNoTiles.forEach((fieldId) =>
      invalidateArableFieldData(fieldId, queryClient)
    );

    const fieldsWithNoTiles = getFieldsWithNoTilesFromArray(fields);
    fieldsWithNoTiles.forEach((fieldId) =>
      invalidateFieldData(fieldId, queryClient)
    );

    // Fetch growth rate for the first field (used for farm metrics)
    if (farm.fields && farm.fields.length > 0) {
      try {
        const growthRateResponse = await fetchApi(
          `fields/${farm.fields[0]}/growthRate`
        );
        if (
          growthRateResponse &&
          typeof growthRateResponse.growthRate === "number"
        ) {
          const growthRate: number = growthRateResponse.growthRate;
          useUIStore.getState().setGrowthRate(growthRate);
        }
      } catch (error) {
        // Growth rate fetch is not critical, just log and continue
        console.warn("Failed to fetch growth rate:", error);
      }
    }
  })();

  // Mark loading complete
  setLoadingProgress(APP_LOADING_STEPS, APP_LOADING_STEPS);
  setLoadingStatus("");
  setLoading("idle");
}

/**
 * Fetch a farm by ID using React Query.
 * This is a standalone fetch that doesn't do full initialization.
 *
 * @param farmId - The farm ID to fetch
 * @returns The fetched farm
 */
export async function fetchFarmByIdRQ(farmId: string): Promise<Farm> {
  return await fetchFarmByIdQuery(farmId, queryClient);
}

/**
 * Fetch animal groups by their IDs using React Query.
 *
 * @param animalGroupIds - Array of animal group IDs to fetch
 * @returns The fetched animal groups
 */
export async function fetchAnimalGroupsByIdRQ(
  animalGroupIds: string[]
): Promise<Awaited<ReturnType<typeof fetchAnimalGroupsByIdQuery>>> {
  return await fetchAnimalGroupsByIdQuery(animalGroupIds, queryClient);
}

/**
 * Finish loading state in Zustand.
 */
export function finishLoadingStateRQ(): void {
  const { setLoading, setLoadingStatus, setLoadingProgress } =
    useAppStore.getState();
  setLoading("idle");
  setLoadingStatus("");
  setLoadingProgress(0, 0);
}

/**
 * Switch to a different farm using React Query.
 * Fetches the farm and initializes it.
 *
 * @param farmId - The ID of the farm to switch to
 */
export async function switchFarmRQ(farmId: string): Promise<void> {
  const farm = await fetchFarmByIdQuery(farmId, queryClient);
  await initializeFarmRQ(farm);
}
